import * as React from 'react';
import {
    Datagrid,
    DateField,
    DateInput,
    List,
    ListProps,
    NumberField,
    ReferenceField,
    TextField,
    Pagination,
    PaginationProps,
    AutocompleteInput,
    ReferenceInput,
    SelectInput,
    NumberInput,
    TextInput,
} from 'react-admin';
import { Box } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';

import TransactionsListAside from './TransactionsListAside';
import { ReactElement } from 'react';

import TransactionTypes from './TransactionTypes'
import { CurrencyFormatInput, dateLocales, currencyFormatter, defaultDateOptions } from '../utils/formatters';
import ListActions from '../components/ToolbarActions';
import CustomNumberField from '../components/customNumber';
import BulkActionButtons from '../components/BulkActions';
import ColoredChipField from '../components/ColoredChip';

const useStyles = makeStyles(theme => ({
    amount: {},
    openIcon: { width: '1rem', height: '1rem' },
    chip: (type: any) => ({
        backgroundColor: type.color,
        color: '#FFF'
    })
}));

const transactionTypes = TransactionTypes()

const transactionFilters = [
    <NumberInput source="id" alwaysOn />,
    <ReferenceInput
        source="merchant_id"
        reference="Merchant"
        sort={{ field: 'name', order: 'ASC' }}
        alwaysOn
        perPage={10}
        filterToQuery={name => ({ name })}
    >
        <AutocompleteInput source="name" resettable />
    </ReferenceInput>,
    <SelectInput source="type@_eq" choices={transactionTypes} resettable alwaysOn />,
    <DateInput source="timestamp@_gte" />,
    <DateInput source="timestamp@_lte" />,
    <ReferenceInput
        source="financing_id"
        reference="Financing"
        perPage={10}
        sort={{ field: 'id', order: 'DESC' }}
        filterToQuery={id => ({ id })}
    >
        <NumberInput source="id" />
    </ReferenceInput>,
    <TextInput
        source="value@_gte"
        InputProps={{
            inputComponent: CurrencyFormatInput,
        }}
    />,
    <ReferenceInput
        source="Financing#platform_id@_eq"
        label="resources.Transaction.fields.platform"
        reference="Platform"
        sort={{ field: 'name', order: 'ASC' }}
        alwaysOn
        perPage={10}
        filterToQuery={name => ({ name })}
    >
        <SelectInput optionText="name" resettable />
    </ReferenceInput>,
    <TextInput
        source="value@_lte"
        InputProps={{
            inputComponent: CurrencyFormatInput,
        }}
    />,
]

const TransactionsList = (props: ListProps): ReactElement => {
    const classes = useStyles({});

    const CustomPagination = (props: JSX.IntrinsicAttributes & PaginationProps) =>
        <Pagination rowsPerPageOptions={[10, 25, 50, 100]} {...props} />;

    return (
        <List
            {...props}
            component={Box}
            filters={transactionFilters}
            sort={{ field: 'timestamp', order: 'DESC' }}
            perPage={50}
            actions={React.cloneElement(<ListActions />, { importer: false })}
            bulkActionButtons={<BulkActionButtons />}
            pagination={<CustomPagination />}
            aside={<TransactionsListAside />}
        >
            <Datagrid optimized rowClick="edit">
                <NumberField source="id" />
                <DateField source="timestamp" locales={dateLocales} options={defaultDateOptions} showTime />
                <ColoredChipField size="small" source="type" choices={transactionTypes}/>
                <ReferenceField source="financing_id" reference="Financing">
                    <NumberField className={classes.openIcon} source="id" />
                </ReferenceField>
                <CustomNumberField
                    source="value"
                    divisor={100}
                    className={classes.amount}
                    locales="en-US"
                    options={currencyFormatter().resolvedOptions()}
                />
                <CustomNumberField
                    source="transfer_to_merchant"
                    divisor={100}
                    className={classes.amount}
                    locales="en-US"
                    options={currencyFormatter().resolvedOptions()}
                    emptyText="-"
                />
                <ReferenceField source="merchant_id" reference="Merchant">
                    <TextField source="name" />
                </ReferenceField>
            </Datagrid>
        </List>
    );
};

export default TransactionsList;
